import React, { useEffect, useState } from 'react'
import styles from '../ipTv.jcss'
import translate from '../../../core/translate'
import { Header } from '../__header/Header'
import { cn } from 'ethcss'
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import Icon from '../../../blocks.simple/icon/icon'
import Checkbox from '../../../blocks.simple/checkbox/checkbox'
import Button from '../../../blocks.simple/button/button'
import { Search } from '../__search/Search'
import { GroupAdd } from '../__groupAdd/GroupAdd'
import { api } from '../../../core/api/ConnectionManager'
import InfiniteScroll from 'react-infinite-scroller'
import LoaderLazy from 'blocks.app/loader/_lazy/loader__lazy'
import Tippy from 'blocks.simple/tippy/tippy'
import { IChannel } from '../__channels/Channels'
import { changeLocation, routes } from '../../../features/routes'
import { getURLSearchParamsString } from '../../../features/routes/utils'

export interface IGroup {
    id?: number
    title: string
    channels: IChannel[]
    name?: string
}

export const Groups = () => {
    const [groupList, setGroupList] = useState<any[]>([])
    const [selectedItems, setSelectedItems] = useState<number[]>([])
    const [groupData, setGroupData] = useState<IGroup | null>(null)
    const [search, setSearch] = useState<string>('')
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null)
    const [isLinkedWarning, setIsLinkedWarning] = useState<boolean>(false)
    const [multipleDeleteConfirm, setMultipleDeleteConfirm] = useState<boolean>(false)
    const [nextPageTokenRes, setNextPageTokenRes] = useState<string>('')
    const [timer, setTimer] = useState<number>(5)

    useEffect(() => {
        if (!isLinkedWarning) return

        const countdown = setInterval(() => {
            setTimer((prevState) => {
                if (prevState === 0) {
                    clearInterval(countdown)

                    return 0
                }

                return prevState - 1
            })
        }, 1000)

        return () => {
            clearInterval(countdown)
            setTimer(5)
        }
    }, [isLinkedWarning])

    useEffect(() => {
        getCategoryList(search, null)
    }, [search])

    const getCategoryList = (query: string, nextPageToken: any) => {
        const data: any =
            nextPageToken === null
                ? {
                      limit: 15,
                      sort: [['order', 'ASC']],
                  }
                : {
                      nextPageToken: nextPageToken,
                      limit: 10,
                  }

        if (query) {
            data.query = query
        }

        api.send('iptvGroupList', data).then((res: any) => {
            if (!res && !res.data) return

            setNextPageTokenRes(res.nextPageToken)

            if (nextPageToken) {
                setGroupList([...groupList, ...res.data])
            } else {
                setGroupList(res.data)
            }
        })
    }

    const onSelectItem = (id: number) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter((item) => item !== id))
            return
        }

        setSelectedItems([id, ...selectedItems])
    }
    const onDeleteItem = (id: number) => {
        api.send('iptvGroupDelete', { ids: [id] }).then(() => {
            setDeleteConfirm(null)
            setIsLinkedWarning(false)
        })
    }
    const deleteAllSelected = () => {
        api.send('iptvGroupDelete', { ids: selectedItems }).then(() => {
            setMultipleDeleteConfirm(false)
            setIsLinkedWarning(false)
        })
    }
    const onCancelDelete = () => {
        setDeleteConfirm(null)
        setMultipleDeleteConfirm(false)
        setIsLinkedWarning(false)
    }
    const onDeleteConfirm = (id: number) => {
        setDeleteConfirm(id)
    }
    const onMultipleDeleteConfirm = () => {
        setMultipleDeleteConfirm(true)

        const list = groupList.filter((group) => selectedItems.includes(group.id))

        if (list.some((item) => item.digitalSignage && item.digitalSignage.length)) {
            setIsLinkedWarning(true)
        }
    }
    const selectAll = () => {
        setSelectedItems(groupList.map((group: any) => group.id))
    }
    const clearSelection = () => {
        setSelectedItems([])
    }
    const dropdownSelect = (select: any) => {
        if (select.id === 'selectAll') {
            selectAll()
            return
        }

        clearSelection()
    }
    const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        const sortedList = arrayMove(groupList, oldIndex, newIndex).slice()
        setGroupList(sortedList)
        api.send('iptvGroupUpdate', { id: sortedList[newIndex].id, order: newIndex })
    }
    const onAddGroup = () => {
        setGroupData({
            title: '',
            channels: [],
        })
    }
    const onEditCategory = (group: IGroup) => {
        setGroupData(group)
    }
    const onCategoryChange = (field: string, value: any) => {
        if (groupData) {
            setGroupData({
                ...groupData,
                [field]: value,
            })
        }
    }
    const onCloseModal = () => {
        setGroupData(null)
    }
    const onGroupCreated = (group: IGroup) => {
        setGroupList([group, ...groupList])
    }
    const onGroupUpdated = (group: IGroup) => {
        setGroupList(
            groupList.map((el: IGroup) => {
                if (el.id === group.id) {
                    return group
                }

                return el
            })
        )
    }
    const onGroupDeleted = (res: any) => {
        if (!res || !res.ids) return

        setGroupList(groupList.filter((group) => !res.ids.includes(group.id)))
    }
    const onSaveGroup = () => {
        if (!groupData) return

        if (groupData.id) {
            api.send('iptvGroupUpdate', {
                id: groupData.id,
                title: groupData.title,
                channels: groupData.channels,
            }).then(onCloseModal)

            return
        }

        api.send('iptvGroupCreate', {
            title: groupData.title,
            channels: groupData.channels,
        }).then(onCloseModal)
    }

    const viewLinkedDevices = (group: { id: number; title: string }) => {
        changeLocation({
            pathname: `/${routes.displays.path}`,
            search: getURLSearchParamsString({
                displays__filterList: JSON.stringify([
                    {
                        name: 'ipTvGroup',
                        type: 'select',
                        options: [
                            {
                                value: group.id,
                                label: group.title,
                                selected: true,
                            },
                        ],
                    },
                ]),
            }),
        })
    }

    const SortableList = SortableContainer(({ items }: any) => (
        <ul className={styles.listWrapper}>
            {items.map((group: any, index: number) => (
                <SortableItem
                    group={group}
                    key={group.id}
                    index={index}
                    onDelete={onDeleteItem}
                    onSelect={onSelectItem}
                />
            ))}
        </ul>
    ))
    const DragHandle = SortableHandle(() => (
        <div className={styles.handle}>
            <Icon type={'sortable_handle'} />
        </div>
    ))
    const SortableItem = SortableElement(({ group }: any) => {
        return (
            <li className={styles.group}>
                <div className={styles.checkbox}>
                    <DragHandle />
                    <Checkbox checked={selectedItems.includes(group.id)} onClick={() => onSelectItem(group.id)} />
                </div>
                <div className={styles.groupName}>
                    <Tippy title={group.title} disabled={group.title.length < 16}>
                        {' '}
                        {group.title}
                    </Tippy>
                </div>
                <div className={styles.groupDevicesCount}>{group.channels ? group.channels.length : 0}</div>
                <div className={styles.categoryInUse}>
                    {group.digitalSignage && group.digitalSignage.length ? (
                        <div
                            className={styles.viewN}
                            onClick={() => {
                                viewLinkedDevices(group)
                            }}
                        >
                            {translate('viewN')}
                        </div>
                    ) : (
                        '—'
                    )}
                </div>
                <div className={styles.changeButtons}>
                    <div>
                        <Icon type={'pencil'} onClick={() => onEditCategory(group)} />
                    </div>
                    <div>
                        <Icon
                            type={'delete'}
                            onClick={() => {
                                if (group.digitalSignage && group.digitalSignage.length) {
                                    setIsLinkedWarning(true)
                                }

                                onDeleteConfirm(group.id)
                            }}
                        />
                    </div>
                </div>
            </li>
        )
    })

    useEffect(() => {
        const listenersId: string[] = []
        api.addObserver('iptvGroupCreated', onGroupCreated, listenersId)
        api.addObserver('iptvGroupUpdated', onGroupUpdated, listenersId)
        api.addObserver('iptvGroupDeleted', onGroupDeleted, listenersId)

        return () => listenersId.forEach((id) => api.removeObserver(id))
    }, [groupList])

    return (
        <>
            <Search search={search} setSearch={setSearch} dropdownSelect={dropdownSelect} />
            <div className={styles.wrapper}>
                <Header
                    selectedItemsLength={selectedItems.length}
                    deleteAllSelected={onMultipleDeleteConfirm}
                    onAdd={onAddGroup}
                />
                <div className={cn(styles.group, styles.categoriesHeader)}>
                    <div className={styles.checkbox}>
                        <span className={styles.checkboxHeader}>{translate('choice')}</span>
                    </div>
                    <div className={styles.groupName}>{translate('name')}</div>
                    <div className={styles.groupDevicesCount}>{translate('channelsCount')}</div>
                    <div className={styles.categoryInUse}>{translate('inUse')}</div>
                </div>
                <InfiniteScroll
                    loadMore={() => getCategoryList(search, nextPageTokenRes)}
                    hasMore={nextPageTokenRes !== null}
                    initialLoad={false}
                    loader={<LoaderLazy key="iptvCategories_loader" active />}
                    threshold={10}
                >
                    <SortableList items={groupList} useDragHandle={true} onSortEnd={onSortEnd} />
                </InfiniteScroll>
            </div>
            {groupData && (
                <GroupAdd group={groupData} onChange={onCategoryChange} onClose={onCloseModal} onSave={onSaveGroup} />
            )}
            {(deleteConfirm || multipleDeleteConfirm) && (
                <div className={styles.deleteConfirmWrapper}>
                    <div className={styles.deleteConfirm}>
                        {isLinkedWarning ? (
                            <div>
                                {translate(
                                    deleteConfirm ? 'deleteGroupConfirmWithLinked' : 'deleteGroupsConfirmWithLinked'
                                )}
                            </div>
                        ) : (
                            <div>{translate(deleteConfirm ? 'deleteGroupConfirm' : 'deleteGroupsConfirm')}</div>
                        )}
                        {isLinkedWarning && (
                            <div className={styles.modalTimer} style={{ opacity: timer > 0 ? 1 : 0 }}>
                                {timer}
                            </div>
                        )}
                        <div className={styles.controls}>
                            <Button mod={'withBorder'} className={styles.button} onClick={onCancelDelete}>
                                {translate('cancel')}
                            </Button>
                            <Button
                                mod={'fill'}
                                className={styles.button}
                                onClick={() => (deleteConfirm ? onDeleteItem(deleteConfirm) : deleteAllSelected())}
                                disabled={isLinkedWarning && !!timer}
                            >
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
